import type { Block } from '@pageboss/shared';
import { InspectorSection } from './inspector-section';
import { InspectorField } from './inspector-field';
import { SegmentedControl } from './segmented-control';
import { Repeater, RepeaterTitle } from './repeater';
import { IconPicker } from './icon-picker';

type ServicesBlock = Extract<Block, { type: 'services' }>;
type ServicesProps = ServicesBlock['props'];
type ServiceItem = ServicesProps['items'][number];

const COLUMN_OPTIONS = [
  { value: 2 as const, label: '2' },
  { value: 3 as const, label: '3' },
  { value: 4 as const, label: '4' },
];

function newServiceItem(): ServiceItem {
  return {
    title: 'New service',
    description: '',
    icon: '',
    price: '',
  };
}

interface ServicesInspectorProps {
  block: ServicesBlock;
  onChange: (next: ServicesProps) => void;
}

export function ServicesInspector({ block, onChange }: ServicesInspectorProps) {
  const props = block.props;
  const patch = (next: Partial<ServicesProps>) => onChange({ ...props, ...next });

  return (
    <>
      <InspectorSection>
        <div className="flex flex-col gap-4">
          <InspectorField
            label="Title"
            value={props.title ?? ''}
            placeholder="Our services"
            onChange={(v) => patch({ title: v })}
          />
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-foreground">Columns</span>
            <SegmentedControl
              ariaLabel="Columns"
              value={props.columns}
              options={COLUMN_OPTIONS}
              onChange={(v) => patch({ columns: v })}
            />
          </div>
        </div>
      </InspectorSection>

      <InspectorSection label="Items">
        <Repeater
          items={props.items}
          onChange={(items) => patch({ items: items as ServicesProps['items'] })}
          addLabel="Add service"
          makeNew={newServiceItem}
          renderTitle={(item) => <RepeaterTitle>{item.title}</RepeaterTitle>}
          renderForm={(item, _i, update) => (
            <>
              <InspectorField
                label="Title"
                value={item.title}
                onChange={(v) => update({ title: v })}
              />
              <InspectorField
                label="Description"
                value={item.description ?? ''}
                multiline
                rows={2}
                onChange={(v) => update({ description: v })}
              />
              <IconPicker
                label="Icon (optional)"
                value={item.icon ?? ''}
                helperText="Pick one or paste an emoji"
                onChange={(v) => update({ icon: v })}
              />
              <InspectorField
                label="Price (optional)"
                value={item.price ?? ''}
                placeholder="From $120"
                onChange={(v) => update({ price: v })}
              />
            </>
          )}
        />
      </InspectorSection>
    </>
  );
}
